import React from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  PurchaseOrderHeader,
  AccountingType,
  Currency,
  MockSupplier,
  ivaOptions
} from './PurchaseOrderFormData';
import { Calendar } from 'lucide-react';

interface PurchaseOrderHeaderFormProps {
  header: PurchaseOrderHeader;
  onChange: (header: PurchaseOrderHeader) => void;
  suppliers: MockSupplier[];
}

export const PurchaseOrderHeaderForm: React.FC<PurchaseOrderHeaderFormProps> = ({ header, onChange, suppliers }) => {
  const handleSupplierChange = (supplierId: number) => {
    onChange({ ...header, supplierId });
  };

  const handleDateChange = (orderDate: string) => {
    onChange({ ...header, orderDate });
  };

  const handleAccountingTypeChange = (accountingType: AccountingType) => {
    onChange({
      ...header,
      accountingType,
      ivaCode: accountingType === 'B' ? null : header.ivaCode
    });
  };

  const handleIvaCodeChange = (ivaCode: string) => {
    onChange({ ...header, ivaCode });
  };

  const handleCurrencyChange = (currency: Currency) => {
    onChange({ ...header, currency });
  };

  return (
    <div className="overflow-hidden border rounded-lg shadow-sm bg-card border-border">
      <div className="px-4 py-5 border-b sm:px-6 border-border">
        <h3 className="flex items-center gap-2 text-lg font-semibold text-foreground">
          <Calendar className="w-5 h-5 text-primary" />
          Datos de la Orden
        </h3>
        <p className="mt-2 text-sm text-muted-foreground">
          Información general de la orden de compra
        </p>
      </div>

      <div className="px-4 py-6 sm:px-6 space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* Proveedor */}
          <div className="space-y-2">
            <Label className="text-xs font-medium text-foreground sm:text-sm">
              Proveedor <span className="text-xs text-red-500">(Campo obligatorio)</span>
            </Label>
            <Select
              value={header.supplierId === 0 ? '' : String(header.supplierId)}
              onValueChange={(value) => handleSupplierChange(Number(value))}
            >
              <SelectTrigger className="rounded-lg bg-background border-border">
                <SelectValue placeholder="Seleccionar proveedor..." />
              </SelectTrigger>
              <SelectContent sortAlphabetically>
                {suppliers.map((supplier) => (
                  <SelectItem key={supplier.id} value={String(supplier.id)}>
                    {supplier.name} — {supplier.cuit}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Fecha */}
          <div className="space-y-2">
            <Label htmlFor="orderDate" className="text-xs font-medium text-foreground sm:text-sm">
              Fecha de Orden <span className="text-xs text-red-500">(Campo obligatorio)</span>
            </Label>
            <Input
              id="orderDate"
              type="date"
              value={header.orderDate}
              onChange={(e) => handleDateChange(e.target.value)}
              className="rounded-lg bg-background border-border focus-visible:ring-primary"
            />
          </div>

          {/* Tipo de Contabilidad */}
          <div className="space-y-2">
            <Label className="text-xs font-medium text-foreground sm:text-sm">
              Tipo de Contabilidad <span className="text-xs text-red-500">(Campo obligatorio)</span>
            </Label>
            <div className="flex gap-3">
              <label
                className="flex items-center flex-1 p-3 transition-all border-2 rounded-lg cursor-pointer hover:bg-accent"
                style={{ borderColor: header.accountingType === 'A' ? 'hsl(var(--primary))' : 'hsl(var(--border))' }}
              >
                <input
                  type="radio"
                  name="accountingType"
                  value="A"
                  checked={header.accountingType === 'A'}
                  onChange={(e) => handleAccountingTypeChange(e.target.value as AccountingType)}
                  className="w-4 h-4 mr-2"
                />
                <span className="text-sm font-medium text-foreground">Tipo A</span>
              </label>
              <label
                className="flex items-center flex-1 p-3 transition-all border-2 rounded-lg cursor-pointer hover:bg-accent"
                style={{ borderColor: header.accountingType === 'B' ? 'hsl(var(--primary))' : 'hsl(var(--border))' }}
              >
                <input
                  type="radio"
                  name="accountingType"
                  value="B"
                  checked={header.accountingType === 'B'}
                  onChange={(e) => handleAccountingTypeChange(e.target.value as AccountingType)}
                  className="w-4 h-4 mr-2"
                />
                <span className="text-sm font-medium text-foreground">Tipo B</span>
              </label>
            </div>
          </div>

          {/* IVA (solo si tipo A) */}
          {header.accountingType === 'A' && (
            <div className="space-y-2">
              <Label className="text-xs font-medium text-foreground sm:text-sm">
                IVA <span className="text-xs text-red-500">(Campo obligatorio)</span>
              </Label>
              <Select
                value={header.ivaCode || ''}
                onValueChange={(value) => handleIvaCodeChange(value)}
              >
                <SelectTrigger className="rounded-lg bg-background border-border">
                  <SelectValue placeholder="Seleccionar IVA..." />
                </SelectTrigger>
                <SelectContent>
                  {ivaOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Moneda */}
          <div className="space-y-2">
            <Label className="text-xs font-medium text-foreground sm:text-sm">
              Moneda <span className="text-xs text-red-500">(Campo obligatorio)</span>
            </Label>
            <div className="flex gap-3">
              <label
                className="flex items-center flex-1 p-3 transition-all border-2 rounded-lg cursor-pointer hover:bg-accent"
                style={{ borderColor: header.currency === 'ARS' ? 'hsl(var(--primary))' : 'hsl(var(--border))' }}
              >
                <input
                  type="radio"
                  name="currency"
                  value="ARS"
                  checked={header.currency === 'ARS'}
                  onChange={(e) => handleCurrencyChange(e.target.value as Currency)}
                  className="w-4 h-4 mr-2"
                />
                <span className="text-sm font-medium text-foreground">$ ARS</span>
              </label>
              <label
                className="flex items-center flex-1 p-3 transition-all border-2 rounded-lg cursor-pointer hover:bg-accent"
                style={{ borderColor: header.currency === 'USD' ? 'hsl(var(--primary))' : 'hsl(var(--border))' }}
              >
                <input
                  type="radio"
                  name="currency"
                  value="USD"
                  checked={header.currency === 'USD'}
                  onChange={(e) => handleCurrencyChange(e.target.value as Currency)}
                  className="w-4 h-4 mr-2"
                />
                <span className="text-sm font-medium text-foreground">USD</span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
